import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api/client';
import { useApi } from '../api/useApi';

// ── Badge helpers ─────────────────────────────────────────────────────────────

const L1_BADGE: Record<string, string> = {
  'Due Fri':  'badge badge-due',
  'Open':     'badge badge-open',
  'Upcoming': 'badge badge-invite',
  'Later':    'badge badge-later',
};

const L2_BADGE: Record<string, string> = {
  'Pending approval':  'badge badge-invite',
  'No response (8d)':  'badge badge-no-response',
  'Declined Aug 22':   'badge badge-no-response',
  'Not started':       'badge badge-later',
  'Confirmed':         'badge badge-confirmed',
  'Awaiting RSVP':     'badge badge-awaiting',
};

function l1BadgeCls(status: string) { return L1_BADGE[status] ?? 'badge badge-later'; }
function l2BadgeCls(status: string) { return L2_BADGE[status] ?? 'badge badge-later'; }

// ── Sub-components ────────────────────────────────────────────────────────────

function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="ai-progress-wrap">
      <div className="ai-progress-bar">
        <div className="ai-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="ai-progress-txt">{done} / {total} done</span>
    </div>
  );
}

function AiDot({ label }: { label: string }) {
  return (
    <div className="ai-indicator">
      <span className="ai-indicator-dot" />
      <span className="ai-indicator-label">{label}</span>
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <span
      className="action-chevron"
      style={{ transform: open ? 'rotate(180deg)' : 'none' }}
    >
      ⌄
    </span>
  );
}

// ── L2 row ────────────────────────────────────────────────────────────────────

function L2Row({ child, navigate }: { child: any; navigate: (p: string) => void }) {
  const isDimmed = child.dimmed === true;
  return (
    <div
      className={`action-l2-row${isDimmed ? ' action-l2-dimmed' : ''}`}
      onClick={() => !isDimmed && child.fi && navigate(`/fi/${child.fi}`)}
      title={isDimmed ? undefined : child.fiName}
    >
      <div className="action-date" style={{ fontSize: 11 }}>{child.date ?? '—'}</div>
      <div className="action-l2-label-wrap">
        <span className="action-l2-connector">└</span>
        <div>
          <div className="action-l2-fi" style={isDimmed ? { color: '#888780' } : undefined}>
            {child.fiName}
          </div>
          <div className="action-l2-action">{child.action}</div>
        </div>
      </div>
      <div>
        {child.aiDraft && child.aiLabel ? <AiDot label={child.aiLabel} /> : null}
      </div>
      <div>
        <span className={l2BadgeCls(child.status)}>{child.status}</span>
      </div>
      <div className="action-l2-arrow" style={isDimmed ? { color: '#ddd' } : undefined}>›</div>
    </div>
  );
}

// ── L1 row ────────────────────────────────────────────────────────────────────

function L1Row({
  item,
  expanded,
  onToggle,
  navigate,
}: {
  item: any;
  expanded: boolean;
  onToggle: () => void;
  navigate: (p: string) => void;
}) {
  const hasChildren = item.children && item.children.length > 0;

  return (
    <>
      <div
        className={`action-l1-row${expanded ? ' action-l1-open' : ''}${!hasChildren ? ' action-l1-no-expand' : ''}`}
        onClick={hasChildren ? onToggle : undefined}
        role={hasChildren ? 'button' : undefined}
        aria-expanded={hasChildren ? expanded : undefined}
      >
        <div className="action-date">{item.date}</div>
        <div>
          <div className="action-l1-label">{item.label}</div>
          <div className="action-l1-meta">
            {item.progress ? (
              <ProgressBar done={item.progress.done} total={item.progress.total} />
            ) : (
              <span>{item.meta}</span>
            )}
          </div>
        </div>
        <div className="action-detail">{item.detail}</div>
        <div>
          <span className={l1BadgeCls(item.status)}>{item.status}</span>
        </div>
        <div>
          {hasChildren ? <Chevron open={expanded} /> : null}
        </div>
      </div>

      {hasChildren && expanded && (
        <div className="action-l2-group">
          {item.children.map((child: any) => (
            <L2Row key={child.id} child={child} navigate={navigate} />
          ))}
        </div>
      )}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: kpis, loading: kLoading, error: kError } = useApi<any>(() => API.dashboardKpis());
  const { data: actions, loading: aLoading, error: aError } = useApi<any[]>(() => API.actions());

  // First L1 is expanded by default (matches wireframe)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (kLoading || aLoading) return <div className="phil-loading">Loading…</div>;
  if (kError || aError || !kpis || !actions) {
    return <div className="phil-error">Unable to load dashboard. {kError ?? aError}</div>;
  }

  function toggle(id: string) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <>
      <div className="page-header">

        <h1>FinTech Innovation Lab Dashboard</h1>
        <p className="subtitle">{kpis.dateSubtitle}</p>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-val">{kpis.totalFIs}</div>
          <div className="kpi-label">FI Partners</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-val" style={{ color: '#16a34a' }}>{kpis.activeFIs}</div>
          <div className="kpi-label">Active Partners</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-val" style={{ color: '#2563eb' }}>{kpis.onSelectionCommittee}</div>
          <div className="kpi-label">On Selection Committee</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-val" style={{ color: '#d97706' }}>{kpis.prioritiesSet}</div>
          <div className="kpi-label">FY26 Priorities Set</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Action Items</h2>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/feedback-priorities')}
          >
            View all →
          </button>
        </div>

        <div className="action-table">
          {/* Column headers */}
          <div className="action-col-hd">
            <span>Date</span>
            <span>Action</span>
            <span>Detail</span>
            <span>Status</span>
            <span />
          </div>

          {actions.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#888' }}>
              No outstanding action items at this time.
            </div>
          ) : actions.map((item: any) => (
            <L1Row
              key={item.id}
              item={item}
              expanded={!!expanded[item.id]}
              onToggle={() => toggle(item.id)}
              navigate={navigate}
            />
          ))}
        </div>

        {/* Legend */}
        <div className="action-legend">
          <div className="action-legend-item">
            <span className="ai-indicator-dot" />
            <span>AI draft available</span>
          </div>
          <span className="action-legend-sep">·</span>
          <div className="action-legend-item">Click any row to open FI profile</div>
          <span className="action-legend-sep">·</span>
          <div className="action-legend-item">Chevron expands sub-tasks</div>
        </div>
      </div>

      <div className="mt-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header"><h2>Quick Links</h2></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { label: '→ Feedback & Priority List', path: '/feedback-priorities' },
              { label: '→ Priority Scoring Matrix', path: '/feedback-priorities/scoring' },
            ].map(l => (
              <div
                key={l.path}
                onClick={() => navigate(l.path)}
                style={{ fontSize: 13, color: '#2563eb', cursor: 'pointer', padding: '4px 0' }}
              >
                {l.label}
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h2>FI Partners by Industry</h2></div>
          <div className="card-body">
            {(kpis.seasonProgress ?? []).map((s: any) => (
              <div key={s.label} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4, color: '#4b5563' }}>
                  <span>{s.label}</span>
                  <span style={{ fontWeight: 600 }}>{s.val}/{s.max}</span>
                </div>
                <div style={{ height: 6, background: '#f1f5f9', borderRadius: 100 }}>
                  <div style={{ height: '100%', width: `${(s.val / s.max) * 100}%`, background: s.color, borderRadius: 100 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
